import moment from 'moment';
import * as dates from '@simplybenefits/sb-dates';
import { getDentalHistoryFilters } from '../../actions/dental.js';
import { getDivisionTitleFilterByGroupNum } from '../../actions/divisions.js';
import { getRenewalPeriodsByGroupNum } from './logic/accounts.js';
import { getClassTitleFilterByGroupNum } from '../../actions/classes.js';
import { constants } from '@simplybenefits/sb-utils';
import { getAllAdvisorsForBrokerage } from '../../actions/advisors.js';

const now = new Date();
const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

/**
 *
 * @param brokerage_
 * @returns {function(*): Promise<{[p: string]: any, 'All': null}>}
 */
const getAdvisorFilterByBrokerage = (brokerage_) => async (dispatch) => {
    const brokerage = brokerage_.toLowerCase() === 'all' ? null : brokerage_;
    const { advisors } = await dispatch(getAllAdvisorsForBrokerage(brokerage));
    const advisorIDsByName = advisors.map((e) => [ `${ e.firstname } ${ e.lastname }`, e.advisorID ]);
    return {
        'All': null,
        ...Object.fromEntries(advisorIDsByName)
    };
};

/**
 * Dates can come in as different objects or formats....
 * @param date
 * @returns {*}
 */
const formatDate = (date) => {
    const dateFormat = 'MMM DD, YYYY';
    return date._isAMomentObject === true ? date.format(dateFormat) : dates.formatDateForDisplay(date, dateFormat);
};

const reportsData = {
    'Admin Reports': {
        'Members and Groups': {
            'Onboarding': {
                icon: 'personAdd',
                description: 'Pull a report for Onboarding based on Carrier',
                filters: {
                    carrier: {
                        type: 'select',
                        label: 'Carrier',
                        options: [ 'Advica', 'AIG', 'Allstate', 'ASO', 'Assumption Life', 'CHUBB', 'Desjardins', 'Empire', 'Equitable Life', 'Humania Assurance', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'Kii', 'La Capitale', 'Orion', 'SSQ', 'Sunlife', 'Wawanesa' ],
                        value: 'Empire'
                    },
                    userType: {
                        type: 'bubble-select',
                        label: 'User Type',
                        options: [ 'Members', 'Dependents' ],
                        value: 'Members'
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    }
                }
            },
            'Over Age Member Listing': {
                title: 'Over-age Member Anniversary',
                icon: 'elderly',
                description: 'Pull a report of members turning a certain age within a daterange',
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment(),
                        endDate: moment().add(6, 'month')
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: ''
                    },
                    age: {
                        type: 'number',
                        label: 'Age',
                        isRequired: true,
                        value: 65
                    }
                }
            },
            'Optional Benefits': {
                icon: 'star',
                description: 'Pull a Optional Benefits Report',
                filters: {
                    month: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    type: {
                        type: 'select',
                        label: 'Type',
                        options: [
                            'All',
                            'optLife',
                            'optLifeSpouse',
                            'optLifeChild',
                            'optAdd',
                            'optAddSpouse',
                            'optAddChild',
                            'optCi',
                            'optCiSpouse',
                            'optCiChild'
                        ],
                        value: 'All'
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    }
                }
            },
            'LTD': {
                icon: 'dateRange',
                description: 'Review LTD volume changes and associated premium details for members with recent coverage updates.',
                filters: {
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    }
                }
            },
            'Orion': {
                title: "Orion Out of Country",
                icon: 'flight_takeoff',
                description: 'Pull a report of active members with OOC covered by Orion',
                filters: {
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: false
                    }
                }
            },
            'Allstate CI': {
                icon: 'personAdd',
                description: 'Pull a report for Allstate CI based on mandatory or optional benefits',
                filters: {
                    userType: {
                        type: 'bubble-select',
                        label: 'Benefit Type',
                        options: [ 'Mandatory', 'Optional' ],
                        value: 'Mandatory'
                    }
                }
            },
            'Benefit Carrier': {
                icon: 'handshake',
                description: 'Pull a report to review member, group and/or benefit details related to a specific carrier.',
                hideReportTable: true,
                filters: {
                    carrier: {
                        type: 'select',
                        label: 'Carrier',
                        options: [ 'Advica', 'AIG', 'Allstate', 'ASO', 'Assumption Life', 'Camden', 'CHUBB', 'Desjardins', 'Dialogue', 'Empire', 'Enriched Academy', 'Equitable Life', 'Fenchurch', 'Gotodoctor', 'Humania Assurance', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'Kii', 'La Capitale', 'Maple', 'Orion', 'Simply Benefits', 'SSQ', 'Sunlife', 'Wawanesa' ],
                        value: 'Empire',
                        isRequired: true
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: false
                    },
                    benefit: {
                        type: 'select',
                        label: 'Benefit',
                        options: [
                            'All',
                            'Life',
                            'Accidental Death Dismemberment',
                            'Dependent Life',
                            'Short Term Disability',
                            'Long Term Disability',
                            'Critical Illness',
                            'Drugs',
                            'Healthcare',
                            'Paramedical',
                            'Vision',
                            'Out Of Country Province',
                            'Employee Family Assistance Program',
                            'Virtual Healthcare',
                            'Financial Awareness',
                            'Dental'
                        ],
                        value: 'All',
                        isRequired: true
                    },
                    additionalData: {
                        type: 'bubble-list-selector',
                        label: 'Please select any additional details that you would like to be included in the report:',
                        options: [
                            'Additional Member Details',
                            'Account Details',
                            'Dependent Details',
                            'Beneficiary Details',
                            'Trustee Details'
                        ],
                        value: [],
                        isRequired: true
                    }
                }
            },
            'NEM Eligibility': {
                icon: 'grading',
                description: 'View NEM eligibility per member.',
                filters: {
                    groupNum: {
                        debounceArr: [
                            {
                                populates: 'divisionOptions',
                                fn: getDivisionTitleFilterByGroupNum
                            },
                            {
                                populates: 'classOptions',
                                fn: getClassTitleFilterByGroupNum
                            }
                        ],
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    },
                    class: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Class',
                        options: [],
                        value: 'All Classes',
                        isRequired: true
                    },
                    division: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Division',
                        options: [],
                        value: 'All Divisions',
                        isRequired: true
                    },
                    benefit: {
                        type: 'select',
                        label: 'Benefit',
                        options: [
                            'All',
                            'Life',
                            'Accidental Death Dismemberment',
                            'Short Term Disability',
                            'Long Term Disability'
                        ],
                        value: 'All',
                        isRequired: true
                    },
                    status: {
                        type: 'select',
                        label: 'Status',
                        options: [
                            'All',
                            ...Object.values(constants.memberStatusDropdownMap)
                        ],
                        value: 'All',
                        isRequired: true
                    }
                }
            }
        },
        'Claims': {
            'Claims Adjudication': {
                icon: 'verifiedUser',
                description: 'Pull a Claims Adjudication Report',
                hideReportTable: true,
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    type: {
                        type: 'bubble-select',
                        label: 'Date Range Filter Type',
                        options: [ 'Date Added', 'Paid Date' ],
                        value: 'Date Added'
                    },
                    carrier: {
                        type: 'select',
                        label: 'Carrier',
                        options: [ 'Advica', 'AIG', 'All', 'Allstate', 'ASO', 'Assumption Life', 'CHUBB', 'Desjardins', 'Empire', 'Equitable Life', 'Humania Assurance', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'Kii', 'La Capitale', 'Orion', 'SSQ', 'Sunlife', 'Wawanesa' ],
                        value: 'All'
                    },
                    benefit: {
                        type: 'select',
                        label: 'Benefit',
                        options: [
                            'All',
                            'Critical Illness',
                            'Death & Dismemberment',
                            'Dependent Life',
                            'Long Term Disability',
                            'Short Term Disability',
                            'Life Insurance',
                            'Drugs',
                            'Dental',
                            'Health',
                            'Paramedicals',
                            'Vision',
                            'Out of Country',
                            'Employee Assistance',
                            'Spending Accounts',
                            'Lifestyle Spending',
                            'Health Spending',
                            'Virtual Healthcare',
                            'Financial Awareness'
                        ],
                        value: 'All'
                    },
                    status: {
                        type: 'select',
                        label: 'Status',
                        options: [
                            'All',
                            'Pending',
                            'Referred',
                            'Approved',
                            'Paid',
                            'Declined',
                            'Stopped'
                        ],
                        value: 'All'
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: ''
                    },
                    className: {
                        type: 'text',
                        label: 'Class Name',
                        value: ''
                    },
                    divisionName: {
                        type: 'text',
                        label: 'Division Name',
                        value: ''
                    }
                }
            },
            'Dental History': {
                icon: 'history',
                description: ' View details about all dental procedures claimed by a certificate',
                showSaveButton: false,
                initialFetchResults: {
                    certMeta: {
                        buttonText: 'Load Member',
                        fn: getDentalHistoryFilters,
                        type: 'number',
                        label: 'Certificate Number',
                        value: '',
                        isRequired: true
                    }
                },
                filters: {
                    patient: {
                        dependsOn: 'certMeta',
                        type: 'select',
                        label: 'Patient',
                        options: [],
                        value: 'All',
                        isRequired: true
                    }
                }
            },
            'Paid Claim Summary': {
                icon: 'barCharts',
                description: 'Pull a report to view a summary of benefit utilization based on claims.',
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment(),
                        isRequired: true
                    },
                    dateRangeType: {
                        type: 'bubble-select',
                        label: 'Date Range Filter Type',
                        options: [ 'Service Date', 'Paid Date' ],
                        value: 'Service Date',
                        isRequired: true
                    },
                    benefit: {
                        type: 'select',
                        label: 'Benefit',
                        options: [
                            'All',
                            'Dental',
                            'Drugs',
                            'Health',
                            'Paramedicals',
                            'Vision'
                        ],
                        value: 'All',
                        isRequired: true
                    },
                    groupNum: {
                        debounceArr: [
                            {
                                populates: 'divisionOptions',
                                fn: getDivisionTitleFilterByGroupNum
                            },
                            {
                                populates: 'classOptions',
                                fn: getClassTitleFilterByGroupNum
                            }
                        ],
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    },
                    class: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Class',
                        options: [],
                        value: 'All Classes',
                        isRequired: true
                    },
                    division: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Division',
                        options: [],
                        value: 'All Divisions',
                        isRequired: true
                    }
                }
            }
        }
    },
    'Accounting Reports': {
        'Daily Reports': {
            'Reimbursements': {
                icon: 'attachMoney',
                description: 'View past reimbursements.',
                hideReportTable: true,
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    benefit: {
                        type: 'select',
                        label: 'Benefit',
                        options: [
                            'All',
                            'Critical Illness',
                            'Death & Dismemberment',
                            'Dependent Life',
                            'Long Term Disability',
                            'Short Term Disability',
                            'Life Insurance',
                            'Drugs',
                            'Dental',
                            'Health',
                            'Paramedicals',
                            'Vision',
                            'Out Of Country',
                            'Employee Assistance',
                            'Health Spending',
                            'Lifestyle Spending',
                            'Cost Plus'
                        ],
                        value: 'All'
                    },
                    type: {
                        type: 'select',
                        label: 'Type',
                        options: [ 'All', 'HSA', 'LSA', 'Standard' ],
                        value: 'All'
                    }
                }
            },
            'EClaims Provider Reimbursements': {
                icon: 'attachMoney',
                description: 'View past reimbursements for EClaims Providers.',
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    benefit: {
                        type: 'select',
                        label: 'Benefit',
                        options: [
                            'All',
                            'Paramedicals',
                            'Vision'
                        ],
                        value: 'All'
                    }
                }
            },
            'Instream': {
                icon: 'healing',
                description: 'Pull a report for Instream Payments',
                hideReportTable: true,
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    }
                }
            }
        },
        'Monthly Reports': {
            'Member-Pay Invoice': {
                icon: 'receiptIcon',
                description: 'Pull a report to review member-pay invoices for the provided month',
                titleExpansionKeys: {
                    monthAndYear: { format: (date) => dates.formatDateForDisplay(date, 'MMM YYYY') },
                    groupNum: { default: 'All Groups' },
                    division: { default: 'All Divisions' }
                },
                hideReportTable: true,
                filters: {
                    monthAndYear: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    groupNum: {
                        debounceArr: [{
                            populates: 'divisionOptions',
                            fn: getDivisionTitleFilterByGroupNum
                        }],
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: false
                    },
                    division: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Division',
                        options: [],
                        value: 'All Divisions',
                        isRequired: false
                    }
                }
            },
            'ASO Monthly Summary': {
                icon: 'adminPanelSettings',
                description: 'View the ASO Monthly Summary',
                filters: {
                    from: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    to: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    },
                    divisionName: {
                        type: 'text',
                        label: 'Division Name',
                        value: ''
                    },
                    benefit: {
                        type: 'select',
                        label: 'Benefit',
                        options: [
                            'Both',
                            'Health',
                            'Dental'
                        ],
                        value: 'Both'
                    }
                }
            },
            'Premium Remittance 2.0': {
                icon: 'listAlt',
                description: 'Pull a report to review the value of the collected premium which should be remitted to carriers and advisors',
                hideReportTable: true,
                filters: {
                    month: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: ''
                    }
                }
            },
            'Payment File': {
                icon: 'localAtm',
                description: 'View the Payment File Report For The Current Month (Doesn\'t Pull Records After The 11th)',
                filters: {}
            },
            'Claims Remittance': {
                icon: 'requestQuote',
                description: 'Pull a Claims Remittance Report for Assumption Life, Desjardins, Empire, Humania Assurance, Industrial Alliance, Industrial Alliance Special Risk, La Capitale or Wawanesa',
                hideReportTable: true,
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    carrier: {
                        type: 'select',
                        label: 'Carrier',
                        options: [ 'Assumption Life', 'Desjardins', 'Empire', 'Humania Assurance', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'La Capitale', 'Wawanesa' ],
                        value: 'La Capitale'
                    },
                    type: {
                        type: 'bubble-select',
                        label: 'Date Range Filter Type',
                        options: [ 'Date Added', 'Paid Date' ],
                        value: 'Date Added'
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: ''
                    }
                }
            },
            'EFAP': {
                icon: 'groups',
                description: 'Pull a report for EFAP charges by carrier.',
                filters: {
                    month: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: ''
                    },
                    carrier: {
                        type: 'select',
                        label: 'Carrier',
                        options: [ 'Kii', 'Advica' ],
                        value: 'Kii'
                    }
                }
            },
            'Commissions': {
                icon: 'savings',
                description: 'Pull a report for Commissions',
                hideReportTable: true,
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    brokerage: {
                        type: 'select',
                        label: 'Brokerage',
                        options: [ 'All' ],
                        value: 'All'
                    },
                    status: {
                        type: 'select',
                        label: 'Status',
                        options: [ 'All', 'Pending', 'Paid' ],
                        value: 'All'
                    }
                }
            },
            'Loss Ratio': {
                icon: 'compareArrows',
                description: 'View the loss ratio report.',
                testid: 'loss-ratio',
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    },
                    divisionName: {
                        type: 'text',
                        label: 'Division Name',
                        value: ''
                    }
                }
            },
            'Outstanding Premium': {
                icon: 'moneyOffIcon',
                description: 'Pull a report for Outstanding Premium (groups or members).',
                testid: 'outstanding-premium',
                filters: {
                    type: {
                        type: 'bubble-select',
                        label: 'Sort outstanding premium by',
                        options: [ 'Groups', 'Members' ],
                        value: 'Groups'
                    }
                }
            },
            'Adjustments': {
                icon: 'currencyExchangeIcon',
                description: 'View a detailed breakdown of pending and completed billing adjustment amounts for a group or certificate.',
                testid: 'adjustments',
                filters: {
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    },
                    cert: {
                        type: 'number',
                        label: 'Cert',
                        value: ''
                    }
                }
            },
            'Division Billing': {
                icon: 'switchAccount',
                description: 'Pull a report to view a summary of the divisional charges for a month.',
                titleExpansionKeys: {
                    monthAndYear: { format: (date) => dates.formatDateForDisplay(date, 'MMM YYYY') },
                    groupNum: { default: 'All Groups' },
                    division: { default: 'All Divisions' }
                },
                hideReportTable: true,
                filters: {
                    monthAndYear: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    groupNum: {
                        debounceArr: [{
                            populates: 'divisionOptions',
                            fn: getDivisionTitleFilterByGroupNum
                        }],
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: false
                    },
                    division: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Division',
                        options: [],
                        value: 'All Divisions',
                        isRequired: false
                    }
                }
            }, 
            'Floats': {
                icon: 'savings',
                description: 'Pull a report to view divisional float balances.',
                titleExpansionKeys: {
                    groupNum: { default: 'All Groups' },
                    division: { default: 'All Divisions' }
                },
                filters: { 
                    type: {
                        type: 'bubble-select',
                        label: 'Float Type',
                        options: [ 'Spending Account', 'ASO In Arrears' ],
                        value: 'Spending Account'
                    },
                    groupNum: {
                        debounceArr: [{
                            populates: 'divisionOptions',
                            fn: getDivisionTitleFilterByGroupNum
                        }],
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: false
                    },
                    division: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Division',
                        options: [],
                        value: 'All Divisions',
                        isRequired: false
                    }
                }
            },
            'Commission by Division': {
                titleExpansionKeys: {
                    from: { format: (date) => dates.formatDateForDisplay(date, 'MMM YYYY') },
                    to: { format: (date) => dates.formatDateForDisplay(date, 'MMM YYYY') },
                    groupNum: {},
                    division: { default: 'All Divisions' }
                },
                icon: 'priceCheck',
                description: 'Pull a report to see the commission amounts payable for each division of a group.',
                hideReportTable: true,
                testid: 'commission-by-division',
                filters: {
                    from: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    to: {
                        type: 'month',
                        value: firstOfMonth
                    },
                    groupNum: {
                        debounceArr: [
                            {
                                populates: 'divisionOptions',
                                fn: getDivisionTitleFilterByGroupNum
                            }
                        ],
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    },
                    division: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Division',
                        options: [],
                        value: 'All Divisions',
                        isRequired: true
                    }
                }
            }
        }
    }, 
    'Underwriting Reports': {
        'Renewal Reports': {
            'Renewal Dates': {
                icon: 'autorenew',
                description: 'Pull a report to see all current and upcoming renewals.',
                filters: {
                    brokerage: {
                        followUpRequests: [{
                            resetFilterName: 'advisor',
                            resetFilterValue: 'All',
                            populates: 'advisorOptions',
                            fn: getAdvisorFilterByBrokerage
                        }],
                        type: 'select',
                        label: 'Brokerage',
                        options: [],
                        value: 'All',
                        isRequired: true
                    },
                    advisor: {
                        populatedBy: 'brokerage',
                        type: 'select',
                        label: 'Advisor',
                        options: [],
                        value: 'All',
                        isRequired: true
                    }
                }
            },
            'Top Drug': {
                icon: 'medication',
                description: 'Identify drugs with the highest accumulated payout amounts.',
                titleExpansionKeys: {
                    dateRange: { format: (date) => [ formatDate(date.startDate), formatDate(date.endDate) ].join(' - ') },
                    groupNum: { default: 'All Groups' },
                    class: { default: 'All Classes' }
                },
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    groupNum: {
                        debounceArr: [
                            {
                                populates: 'classOptions',
                                fn: getClassTitleFilterByGroupNum
                            }
                        ],
                        type: 'number',
                        label: 'Group Number',
                        value: ''
                    },
                    class: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Class',
                        options: [],
                        defaultValue: 'All Classes',
                        value: null
                    },
                    limit: {
                        type: 'select',
                        label: 'Limit',
                        options: [
                            'Top 10',
                            'Top 20',
                            'Top 50',
                            'Top 100'
                        ],
                        value: 'Top 10',
                        isRequired: true
                    }
                }
            },
            'Top Dental Providers': {
                icon: 'badgeIcon',
                description: 'Identify dentists with the highest accumulated claim amounts.',
                titleExpansionKeys: {
                    dateRange: { format: (date) => [ formatDate(date.startDate), formatDate(date.endDate) ].join(' - ') },
                    groupNum: { default: 'All Groups' },
                    class: { default: 'All Classes' },
                    division: { default: 'All Divisions' }
                },
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    groupNum: {
                        debounceArr: [
                            {
                                populates: 'classOptions',
                                fn: getClassTitleFilterByGroupNum
                            },
                            {
                                populates: 'divisionOptions',
                                fn: getDivisionTitleFilterByGroupNum
                            }
                        ],
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    },
                    class: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Class',
                        options: [],
                        value: 'All Classes',
                        isRequired: true
                    },
                    division: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Division',
                        options: [],
                        value: 'All Divisions',
                        isRequired: true
                    },
                    limit: {
                        type: 'select',
                        label: 'Limit',
                        options: [
                            'Top 10',
                            'Top 20'
                        ],
                        value: 'Top 10',
                        isRequired: true
                    }
                }  
            },
            'Demographic Adjustment': {
                icon: 'people',
                description: 'View any demographic shifts that took place during the most recent renewal/experience periods for users holding Life and/or LTD coverage.',
                hideReportTable: true,
                showSaveButton: false,
                requiredFields: [ 'groupNum', 'renewalID' ],
                initialFetchResults: {
                    groupNum: {
                        buttonText: 'Get Renewal Periods',
                        fn: getRenewalPeriodsByGroupNum,
                        type: 'number',
                        label: 'Group Number',
                        value: ''
                    }
                },
                filters: {
                    renewalID: {
                        type: 'select',
                        label: 'Experience Period',
                        options: [],
                        value: "",
                        getValue: (state, key) => state.experiencePeriodOptions[key],
                        isRequired: true
                    }
                }
            },
            'Stop Loss': {
                icon: 'attachMoney',
                description: 'Pull all Drug, Health or OOC claims that were paid over the Stop Loss amount.',
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    groupNum: {
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    }
                }
            },
            'Top Dental Procedures': {
                icon: 'tagFaces',
                description: 'Identify dental procedures with the highest accumulated payout amounts.',
                titleExpansionKeys: {
                    dateRange: {},
                    groupNum: { default: 'All Groups' },
                    class: { default: 'All Classes' },
                    division: { default: 'All Divisions' }
                },
                filters: {
                    dateRange: { 
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    },
                    groupNum: {
                        debounceArr: [
                            {
                                populates: 'divisionOptions',
                                fn: getDivisionTitleFilterByGroupNum
                            },
                            {
                                populates: 'classOptions',
                                fn: getClassTitleFilterByGroupNum
                            }
                        ],
                        type: 'number',
                        label: 'Group Number',
                        value: '',
                        isRequired: true
                    },
                    class: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Class',
                        options: [],
                        value: 'All Classes',
                        isRequired: true
                    },
                    division: {
                        populatedBy: 'groupNum',
                        type: 'select',
                        label: 'Division',
                        options: [],
                        value: 'All Divisions',
                        isRequired: true
                    },
                    limit: {
                        type: 'select',
                        label: 'Limit',
                        options: [ 'Top 10', 'Top 20' ],
                        value: 'Top 10',
                        isRequired: true
                    }

                }
            }
        }
    },
    'Marketplace Reports': {
        'Marketplace Reports': {
            'Marketplace Product List': {
                icon: 'list',
                description: 'View full list of available products.',
                testid: 'marketplace_product_list'
            },
            'Marketplace Open Enrolment': {
                icon: 'accessTime',
                description: 'View the history of open enrolments per product.',
                testid: 'marketplace_open_enrolment_history',
                filters: {
                    productID: {
                        type: 'number',
                        label: 'Product ID',
                        value: ''
                    },
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    }
                }
            },
            'Marketplace Product Purchases': {
                icon: 'category',
                description: 'View all the product purchases within the date range.',
                testid: 'marketplace_purchases',
                filters: {
                    productID: {
                        type: 'number',
                        label: 'Product ID',
                        value: ''
                    },
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    }
                }
            },
            'Marketplace Opt In': {
                icon: 'check',
                description: 'View all the product Opt Ins within the date range for the provided product.',
                testid: 'marketplace_optIns',
                filters: {
                    productID: {
                        type: 'number',
                        label: 'Product ID',
                        value: ''
                    },
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    }
                }
            },
            'Marketplace Opt Out': {
                icon: 'close',
                description: 'View all the product Opt Outs within the date range for the provided product.',
                testid: 'marketplace_optOuts',
                filters: {
                    productID: {
                        type: 'number',
                        label: 'Product ID',
                        value: ''
                    },
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('year'),
                        endDate: moment()
                    }
                }
            }
        }
    },
    'Business Insight Reports': {
        'Sales Reports': {
            // TODO: HERE
            'Block': {
                icon: 'pieChart',
                description: 'Pull a report to review brokerage profitability based on premiums collected and claims paid.',
                testid: 'block',
                hideReportTable: true,
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('month'),
                        endDate: moment()
                    },
                    brokerage: {
                        followUpRequests: [{
                            resetFilterName: 'advisor',
                            resetFilterValue: 'All',
                            populates: 'advisorOptions',
                            fn: getAdvisorFilterByBrokerage
                        }],
                        type: 'select',
                        label: 'Brokerage',
                        options: [],
                        value: 'All',
                        isRequired: true
                    },
                    advisor: {
                        populatedBy: 'brokerage',
                        type: 'select',
                        label: 'Advisor',
                        options: [],
                        value: 'All',
                        isRequired: true
                    }
                }
            }
        },
        'Metrics': {
            'Agent Adjudication Stats': {
                icon: 'verifiedUser',
                description: 'Pull the number of claims and predeterminations for each agent within the date range.',
                filters: {
                    dateRange: {
                        type: 'date',
                        startDate: moment().startOf('day'),
                        endDate: moment(),
                        customOptions: [ 'Today', 'Last 7 days', 'Last 30 days', 'Last 60 days', 'Last 90 days', 'Last 12 months', 'Custom Date Range' ],
                        defaultSelectedValue: 'Today'
                    }
                },
                permissionLevels: [ 'MASTER', 'INSURANCE LEAD' ]
            },
            'Pending Claims': {
                title: 'Pending Claims',
                icon: 'allInbox',
                description: 'Pull a report to review pending claims',
                permissionLevels: [ 'MASTER', 'CSR III', 'ADJUDICATION II', 'PRODUCT TEAM II', 'INSURANCE LEAD' ]
            }
        }
    }
};

export default reportsData;